import type {
    FilterItem,
    StandardFilter as TStandardFilter,
} from '@schibsted-nmp/search-frontend-clients/quest';
import { Toggle } from '@warp-ds/react';
import React from 'react';
import { useFilterSearch } from './useFilterSearch';

type TaxonomyFilterProps = {
    filter: TStandardFilter;
};

export function TaxonomyFilter(props: TaxonomyFilterProps) {
    const { filter } = props;

    const { submitFilterSearch } = useFilterSearch();

    const handleFilterSelect = React.useCallback(
        (filterItem: FilterItem, parentItem?: FilterItem) => {
            submitFilterSearch(filterItem, parentItem);
        },
        [submitFilterSearch],
    );

    return (
        <>
            {filter.filter_items.map((item) => (
                <TaxonomyFilterItem
                    filterItem={item}
                    key={item.value}
                    onSelect={handleFilterSelect}
                />
            ))}
        </>
    );
}

type TaxonomyFilterItemProps = {
    filterItem: FilterItem;
    onSelect: (filterItem: FilterItem, parentItem?: FilterItem) => void;
    parentItem?: FilterItem;
};

function TaxonomyFilterItem(props: TaxonomyFilterItemProps) {
    const { filterItem, parentItem, onSelect } = props;

    const { isSelectedItem, updateSelectedItem } = useSelectedItem(filterItem);

    const handleFilterChange = React.useCallback(() => {
        updateSelectedItem();
        onSelect(filterItem, parentItem);
    }, [filterItem, parentItem, onSelect, updateSelectedItem]);

    const isExpandedItem =
        isSelectedItem &&
        filterItem.filter_items &&
        filterItem.filter_items.length > 0;

    return (
        <>
            <Toggle
                checked={isSelectedItem}
                label={`${filterItem.display_name} (${filterItem.hits})`}
                onChange={handleFilterChange}
                type="checkbox"
            />
            {isExpandedItem && (
                <div className="ml-16">
                    {filterItem.filter_items?.map((item) => (
                        <TaxonomyFilterItem
                            filterItem={item}
                            key={item.value}
                            onSelect={onSelect}
                            parentItem={filterItem}
                        />
                    ))}
                </div>
            )}
        </>
    );
}

function useSelectedItem(filterItem: FilterItem) {
    const [isSelectedItem, setIsSelectedItem] = React.useState(
        filterItem.selected,
    );

    React.useEffect(() => {
        setIsSelectedItem(filterItem.selected);
    }, [filterItem.selected]);

    const updateSelectedItem = React.useCallback(() => {
        setIsSelectedItem((selected) => !selected);
    }, []);

    return { isSelectedItem, updateSelectedItem };
}
